import React from "react";
import { useSelector } from "react-redux";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  Filter as FilterIcon,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useScheduleData,
  type UseScheduleDataReturn,
  type Booking,
  type BlockOut,
  blockOutTypes,
  getBlockOutsForDate,
  getBlockOutsForTimeSlot,
} from "./scheduleData";
import { getMyAppointments } from "@/dispatchers/scheduleEventsDispatchers";
import { ServiceTypeFilter } from "./schedule/ServiceTypeFilter";
import { PharmaciesFilter } from "./schedule/PharmaciesFilter";
import { PharmacistFilter } from "./schedule/PharmacistFilter";
import { RoomFilter } from "./schedule/RoomFilter";
import { BlockOutTypesFilter } from "./schedule/BlockOutTypesFilter";
import { ServiceTypesInfo } from "./schedule/ServiceTypesInfo";
import { BookingModal } from "./schedule/BookingModal";
import { CalendarDayCell } from "./schedule/CalendarDayCell";
import { TimeSlotCell } from "./schedule/TimeSlotCell";
import { BlockOutModal } from "./schedule/BlockOutModal";
import ScheduleSkeleton, { CalendarSkeleton } from "./scheduleSkeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/controls/customControls/dialog";
import { Button as UIButton } from "@/components/controls/customControls/button";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import { accessTitanverseCalenderUpsert } from "@/data/constantStrings";
import { accessTitanverseCalenderView } from "@/data/constantStrings";
import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
export default function Schedule() {
  const s = useScheduleData();
  const location = useLocation();
  const [isFetchingMyAppointments, setIsFetchingMyAppointments] =
    React.useState(false);
  const myAppointmentsData = useSelector(
    (state: any) => state.scheduleEvent?.myAppointments || [],
  );
  const allPharmacies = useSelector(
    (state: any) => state.app?.pharmacies || [],
  );

  const parseLocalDateTime = React.useCallback((dateString: string) => {
    const date = new Date(dateString);
    return {
      date: `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(
        date.getUTCDate(),
      ).padStart(2, "0")}`,
      time: `${String(date.getUTCHours()).padStart(2, "0")}:${String(
        date.getUTCMinutes(),
      ).padStart(2, "0")}`,
    };
  }, []);

  // Destructure everything we use from the hook result, with safe defaults
  const {
    // theme / core
    isDarkMode = false,
    isEventsLoading = false,
    currentDate = new Date(),
    view = "month",
    setView = () => {},
    calendarSelectedDate = new Date(),
    modalState = {
      isOpen: false,
      mode: null,
    } as UseScheduleDataReturn["modalState"],

    // data / labels
    dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    timeSlots = [],
    serviceColors = { Earwax: "", Travel: "", Weight: "", Covid: "" },
    serviceColorsFromData = {},
    pharmacists = [],
    pharmacistNames = [],
    roomNames = [],
    serviceTypes = [],
    serviceTypesInfo = [],
    defaultPharmacyServices = [],

    // filters state
    selectedServiceTypes = [],
    selectedPharmacists = [],
    selectedPharmacies = [],
    selectedRooms = [],
    selectedBlockOutTypes = [],
    currentPharmacyId = "",
    companyId = "",
    myAppointments = [],
    isScheduleEventsByPharmaciesLoading = false,

    // utils
    formatDate = (y: number, m: number, d: number) =>
      `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    formatDateFromDate = (date: Date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(date.getDate()).padStart(2, "0")}`,
    getViewTitle = () => "",
    getDaysInMonth = () => [],
    getWeekDays = () => [],
    getFilteredBookingsForDate = () => [],
    getFilteredBookingsForTimeSlot = () => [],
    getFilteredBlockOutsForDate = () => [],
    getFilteredBlockOutsForTimeSlot = () => [],

    // actions
    navigate = () => {},
    onCalendarSelect = () => {},
    handleViewDay = () => {},
    onCreateBooking = () => {},
    onCreateBookingForDate = () => {},
    onShowEditBooking = () => {},
    onDeleteBooking = () => {},
    onCreateBlockOut = () => {},
    onEditBlockOut = () => {},
    onDeleteBlockOut = () => {},
    onSaveFromModal = () => {},
    closeModal = () => {},
    openNewAppointment = () => {},

    // conflict warning
    conflictWarning = null,
    clearConflictWarning = () => {},

    // filter handlers
    handleServiceTypeChange = () => {},
    handleSelectAllServices = () => {},
    handlePharmacistChange = () => {},
    handleSelectAllPharmacists = () => {},
    handlePharmacyChange = () => {},
    handleSelectAllPharmacies = () => {},
    handleRoomChange = () => {},
    handleSelectAllRooms = () => {},
    handleBlockOutTypeChange = () => {},
    handleSelectAllBlockOutTypes = () => {},
    handleStartConsultation,
    showMyAppointmentsOnly = false,
    setShowMyAppointmentsOnly = () => {},
  } = (s || {}) as Partial<UseScheduleDataReturn>;

  // Focus specific day if provided via route state, query, localStorage, or cross-tab event
  React.useEffect(() => {
    try {
      const stateDate = (location.state as any)?.date;
      const searchParams = new URLSearchParams(location.search);
      const queryDate = searchParams.get("date");
      const stored = localStorage.getItem("calendarRequestedDate") || undefined;
      const dateStr = stateDate || queryDate || stored;
      if (dateStr && typeof handleViewDay === "function") {
        handleViewDay(dateStr);
        try {
          localStorage.removeItem("calendarRequestedDate");
        } catch {}
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const handler = (e: any) => {
      const dateStr = e?.detail?.date;
      if (dateStr && typeof handleViewDay === "function") {
        handleViewDay(dateStr);
      }
    };
    window.addEventListener("calendar:openDay", handler as any);
    return () => window.removeEventListener("calendar:openDay", handler as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const myAppointmentServiceName = "My appointments";
  const myAppointmentsBookings = React.useMemo<Booking[]>(() => {
    return (myAppointments || [])
      .filter((event) => {
        const rawType =
          event?.ScheduleEventType ??
          event?.scheduleEventType ??
          event?.EventType ??
          event?.eventType;
        const scheduleEventType =
          rawType !== undefined && rawType !== null ? Number(rawType) : 1;
        return scheduleEventType === 1;
      })
      .map((event) => {
        const parsed = parseLocalDateTime(event.StartDate);
        const isOtherPharmacy =
          event.PharmacyID && event.PharmacyID !== currentPharmacyId;
        const otherPharmacyName = isOtherPharmacy
          ? allPharmacies.find(
              (p: any) => p.teamPharmacyID === event.PharmacyID,
            )?.teamPharmacyName || ""
          : "";

        const otherPharmacyId = isOtherPharmacy
          ? allPharmacies.find(
              (p: any) => p.teamPharmacyID === event.PharmacyID,
            )?.teamPharmacyID || ""
          : "";

        const service = defaultPharmacyServices.find(
          (s: any) =>
            String(s._id).toLowerCase() ===
            String(event.ServiceID).toLowerCase(),
        );

        return {
          id: event.EventId || event._id,
          consultationID: event.ConsultationID,
          patientName: event.PatientName || "",
          pharmacist: event.CalenderName || "",
          serviceType: "Covid",
          serviceName: service?.serviceName || "",
          serviceId: event.ServiceID || "",
          serviceColor: service?.serviceColor,
          date: parsed.date,
          time: parsed.time,
          duration: event.Duration || 30,
          room: "",
          roomId: event.RoomId || "",
          notes: event.Name || "",
          status: "confirmed",
          contactNumber: event.Mobile || "",
          email: event.Email || "",
          confirmationMethod: event.ConfirmationMethod as
            | "email"
            | "mobile"
            | undefined,
          patientId: event.PatientID || "",
          eventStatus: event.EventStatus,
          isOtherPharmacy,
          otherPharmacyName,
          otherPharmacyId,
          isMyAppointments: true,
        };
      });
  }, [
    parseLocalDateTime,
    myAppointmentsData,
    currentPharmacyId,
    allPharmacies,
  ]);

  const myAppointmentBlockOuts = React.useMemo<BlockOut[]>(() => {
    const mapEventTypeToBlockOutType = (
      eventType: number,
    ): BlockOut["type"] => {
      switch (eventType) {
        case 2:
          return "team_event";
        case 3:
          return "training";
        case 4:
          return "maintenance";
        case 5:
          return "break";
        default:
          return "other";
      }
    };

    const getBlockOutColor = (type: BlockOut["type"]) => {
      return (
        blockOutTypes.find((item) => item.value === type)?.color ||
        "bg-gray-500"
      );
    };

    return (myAppointments || [])
      .filter((event) => {
        const rawType =
          event?.ScheduleEventType ??
          event?.scheduleEventType ??
          event?.EventType ??
          event?.eventType;
        const scheduleEventType =
          rawType !== undefined && rawType !== null ? Number(rawType) : 1;
        return scheduleEventType !== 1;
      })
      .map((event) => {
        const start = parseLocalDateTime(event.StartDate);
        const end = parseLocalDateTime(event.EndDate || event.StartDate);
        const rawType =
          event?.ScheduleEventType ??
          event?.scheduleEventType ??
          event?.EventType ??
          event?.eventType;
        const scheduleEventType =
          rawType !== undefined && rawType !== null ? Number(rawType) : 1;
        const type = mapEventTypeToBlockOutType(scheduleEventType);

        return {
          id: event.EventId || event._id,
          title: event.Name || event.Notes || event.EventType || "Block Out",
          type,
          startDate: start.date,
          endDate: end.date,
          startTime: start.time,
          endTime: end.time,
          description: event.Name || event.Notes || "",
          affectedRooms: event.RoomId ? [event.RoomId] : [],
          affectedPharmacists: event.CalenderName ? [event.CalenderName] : [],
          isRecurring: false,
          recurringPattern: undefined,
          color: getBlockOutColor(type),
          otherPharmacyId: event.PharmacyID || undefined,
        };
      });
  }, [myAppointments, parseLocalDateTime]);

  const getMyAppointmentBlockOutsForDate = React.useCallback(
    (dateString: string) =>
      getBlockOutsForDate(myAppointmentBlockOuts, dateString),
    [myAppointmentBlockOuts],
  );

  const getMyAppointmentBlockOutsForTimeSlot = React.useCallback(
    (dateString: string, hour: number) =>
      getBlockOutsForTimeSlot(myAppointmentBlockOuts, dateString, hour),
    [myAppointmentBlockOuts],
  );

  const getMyAppointmentsForDate = React.useCallback(
    (dateString: string) =>
      myAppointmentsBookings.filter((booking) => booking.date === dateString),
    [myAppointmentsBookings],
  );

  const getMyAppointmentsForTimeSlot = React.useCallback(
    (dateString: string, hour: number) =>
      myAppointmentsBookings.filter((booking) => {
        if (booking.date !== dateString) return false;
        const bookingHour = Number(booking.time.split(":")[0] || "0");
        return bookingHour === hour;
      }),
    [myAppointmentsBookings],
  );

  const extendedServiceColorsFromData = React.useMemo(
    () => ({
      ...serviceColorsFromData,
      [myAppointmentServiceName]: "#10b981",
    }),
    [serviceColorsFromData],
  );

  if (isEventsLoading) {
    return <ScheduleSkeleton isDarkMode={isDarkMode} />;
  }
  const hasUpsertPermission = checkUserPermissions(
    accessTitanverseCalenderUpsert,
  );

  const safe = (fn?: (...args: any[]) => any) => {
    return (...args: any[]) => {
      if (!hasUpsertPermission) {
        setShowPermissionMessage("Create or update calendar events");
        setShowPermissionModal(true);
        return;
      }
      return fn?.(...args);
    };
  };

  return (
    <div
      className={`flex-1 flex h-screen transition-colors duration-300 ${
        isDarkMode ? "bg-gray-900" : "bg-white"
      }`}
    >
      {/* Left Sidebar - Filters - Scrollable */}
      <div
        className={`w-80 border-r p-6 overflow-y-auto max-h-screen transition-colors duration-300 scrollbar-hover-thin ${
          isDarkMode
            ? "border-gray-700 bg-gray-800"
            : "border-gray-200 bg-gray-50"
        }`}
      >
        <div className="space-y-6">
          {/* Small Month Calendar */}
          <div>
            <div
              className={`border rounded-lg overflow-hidden transition-colors duration-300 ${
                isDarkMode
                  ? "border-gray-600 bg-gray-700 [&_.rdp]:text-white [&_.rdp-button]:text-white [&_.rdp-button]:hover:bg-gray-600"
                  : "border-gray-200 bg-white"
              }`}
            >
              <Calendar
                mode="single"
                selected={calendarSelectedDate}
                onSelect={(date) => onCalendarSelect(date || undefined)}
                className="w-full origin-top scale-90"
                classNames={{
                  day_selected: "bg-purple-600 text-white hover:bg-purple-700",
                  day_today: isDarkMode
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-100 text-gray-900",
                  ...(isDarkMode && {
                    months:
                      "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                    month: "space-y-4",
                    caption:
                      "flex justify-center pt-1 relative items-center text-white",
                    caption_label: "text-sm font-medium text-white",
                    nav_button:
                      "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 text-white hover:bg-gray-700 border border-gray-600 rounded",
                    table: "w-full border-collapse space-y-1",
                    head_row: "flex",
                    head_cell:
                      "text-gray-400 rounded-md w-9 font-normal text-xs",
                    row: "flex w-full mt-2",
                    cell: "h-9 w-9 text-center text-sm p-0 relative",
                    day: "h-9 w-9 p-0 font-normal hover:bg-gray-700 text-white rounded",
                    day_outside: "text-gray-600 opacity-50",
                    day_disabled: "text-gray-600 opacity-50",
                  }),
                }}
                components={{
                  DayContent: ({ date }) => {
                    const dateString = formatDateFromDate(date);
                    const dayBookings = showMyAppointmentsOnly
                      ? getMyAppointmentsForDate(dateString)
                      : getFilteredBookingsForDate(dateString);
                    const dayBlockOuts = showMyAppointmentsOnly
                      ? getMyAppointmentBlockOutsForDate(dateString)
                      : getFilteredBlockOutsForDate(dateString);
                    const hasBookings = dayBookings.length > 0;
                    const hasBlockOuts = dayBlockOuts.length > 0;

                    return (
                      <div className="relative flex items-center justify-center w-full h-full">
                        {date.getDate()}
                        <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 flex gap-1">
                          {hasBookings && (
                            <div
                              className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${
                                isDarkMode ? "bg-orange-400" : "bg-orange-500"
                              }`}
                            />
                          )}
                          {hasBlockOuts && (
                            <div
                              className={`w-1.5 h-1.5 transition-colors duration-300 ${
                                isDarkMode ? "bg-red-400" : "bg-red-500"
                              }`}
                              style={{
                                clipPath: "polygon(0 0, 100% 0, 50% 100%)",
                              }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  },
                }}
                onDayClick={(date) => onCalendarSelect(date)}
              />
            </div>
          </div>

          {/* Filters Header */}
          <div className="flex items-center gap-2">
            <FilterIcon
              className={`w-4 h-4 transition-colors duration-300 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            />
            <h3
              className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Filters
            </h3>
          </div>
          <PharmaciesFilter
            selectedPharmacies={selectedPharmacies}
            onPharmacyChange={handlePharmacyChange}
            onSelectAll={handleSelectAllPharmacies}
            isDarkMode={isDarkMode}
          />

          {/* Service Type Filters */}
          <ServiceTypeFilter
            selectedServiceTypes={selectedServiceTypes}
            onServiceTypeChange={handleServiceTypeChange}
            onSelectAll={handleSelectAllServices}
            isDarkMode={isDarkMode}
          />

          {/* Pharmacist Filters */}
          <PharmacistFilter
            selectedPharmacists={selectedPharmacists}
            onPharmacistChange={handlePharmacistChange}
            onSelectAll={handleSelectAllPharmacists}
            isDarkMode={isDarkMode}
          />

          {/* Room Filters */}
          <RoomFilter
            selectedRooms={selectedRooms}
            onRoomChange={handleRoomChange}
            onSelectAll={handleSelectAllRooms}
            isDarkMode={isDarkMode}
          />

          {/* Block-Out Types Filters */}
          <BlockOutTypesFilter
            selectedBlockOutTypes={selectedBlockOutTypes}
            onBlockOutTypeChange={handleBlockOutTypeChange}
            onSelectAll={handleSelectAllBlockOutTypes}
            isDarkMode={isDarkMode}
          />

          {/* Service Types Info */}
          <ServiceTypesInfo isDarkMode={isDarkMode} />
        </div>
      </div>

      {/* Main Content Area - Non-scrollable */}
      <div className="flex flex-col flex-1 max-h-screen overflow-hidden">
        {/* Calendar Header */}
        <div
          className={`border-b p-6 transition-colors duration-300 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center justify-between">
            {/* Left side - Action buttons */}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="p-2 bg-slate-900 hover:bg-slate-800"
                title="New Appointment"
                disabled={!hasUpsertPermission}
                onClick={openNewAppointment}
              >
                <CalendarIcon className="w-4 h-4" />
              </Button>

              <BlockOutModal
                trigger={
                  <Button
                    size="sm"
                    variant="outline"
                    className="p-2 text-orange-600 border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900"
                    title="Block-Out Period"
                  >
                    <Clock className="w-4 h-4" />
                  </Button>
                }
                onSave={onCreateBlockOut}
                isDarkMode={isDarkMode}
              />
            </div>

            {/* Center - Month navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("prev")}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h2
                className={`text-lg font-medium min-w-[200px] text-center transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {getViewTitle()}
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("next")}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Right side - View toggle */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className={`bg-emerald-600 text-white dark:bg-emerald-600 dark:text-white hover:bg-emerald-700 focus-visible:ring-emerald-500 ${showMyAppointmentsOnly ? "ring-2 ring-white/40" : ""}`}
                onClick={async () => {
                  setShowMyAppointmentsOnly((prev) => !prev);
                  // Fetch appointments when toggling on
                  if (!showMyAppointmentsOnly) {
                    setIsFetchingMyAppointments(true);
                    try {
                      await getMyAppointments();
                    } finally {
                      setIsFetchingMyAppointments(false);
                    }
                  }
                }}
              >
                {showMyAppointmentsOnly ? "Back to all" : "My appointments"}
              </Button>
              <Button
                variant={view === "month" ? "default" : "outline"}
                size="sm"
                onClick={() => setView("month")}
              >
                Month
              </Button>
              <Button
                variant={view === "week" ? "default" : "outline"}
                size="sm"
                onClick={() => setView("week")}
              >
                Week
              </Button>
              <Button
                variant={view === "day" ? "default" : "outline"}
                size="sm"
                onClick={() => setView("day")}
              >
                Day
              </Button>
            </div>
          </div>
        </div>

        {/* Calendar Views */}
        {isFetchingMyAppointments || isScheduleEventsByPharmaciesLoading ? (
          <CalendarSkeleton isDarkMode={isDarkMode} />
        ) : (
          <div
            className={`flex-1 p-6 overflow-y-auto transition-colors duration-300 ${
              isDarkMode ? "bg-gray-900" : ""
            }`}
          >
            {view === "month" && (
              <>
                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-px mb-px">
                  {dayNames.map((day) => (
                    <div
                      key={day}
                      className={`p-3 text-center text-sm font-medium transition-colors duration-300 ${
                        isDarkMode
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-50 text-gray-700"
                      }`}
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div
                  className={`grid grid-cols-7 gap-px transition-colors duration-300 ${
                    isDarkMode ? "bg-gray-600" : "bg-gray-200"
                  }`}
                >
                  {getDaysInMonth(currentDate).map((day, index) => {
                    if (day === null) {
                      return (
                        <div
                          key={`empty-${index}`}
                          className={`h-36 p-2 transition-colors duration-300 ${
                            isDarkMode ? "bg-gray-800" : "bg-gray-50"
                          }`}
                        />
                      );
                    }

                    const dateString = formatDate(
                      currentDate.getFullYear(),
                      currentDate.getMonth(),
                      day,
                    );
                    const dayBookings = showMyAppointmentsOnly
                      ? getMyAppointmentsForDate(dateString)
                      : getFilteredBookingsForDate(dateString);
                    const dayBlockOuts = showMyAppointmentsOnly
                      ? getMyAppointmentBlockOutsForDate(dateString)
                      : getFilteredBlockOutsForDate(dateString);
                    const isToday =
                      new Date().toDateString() ===
                      new Date(dateString).toDateString();

                    return (
                      <CalendarDayCell
                        key={day}
                        day={day}
                        dateString={dateString}
                        dayBookings={dayBookings}
                        dayBlockOuts={dayBlockOuts}
                        isToday={isToday}
                        serviceColors={serviceColors}
                        serviceColorsFromData={extendedServiceColorsFromData}
                        onCreateBooking={safe(onCreateBooking)}
                        onEditBooking={safe(onShowEditBooking)}
                        onDeleteBooking={safe(onDeleteBooking)}
                        onEditBlockOut={safe(onEditBlockOut)}
                        onDeleteBlockOut={safe(onDeleteBlockOut)}
                        onCreateBookingForDate={safe(onCreateBookingForDate)}
                        onViewDay={handleViewDay}
                        pharmacistNames={pharmacistNames}
                        rooms={roomNames}
                        isDarkMode={isDarkMode}
                        readOnlyBookings={false}
                      />
                    );
                  })}
                </div>
              </>
            )}

            {view === "week" && (
              <div
                className={`border rounded-lg overflow-hidden transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-600"
                    : "bg-white border-gray-200"
                }`}
              >
                {/* Week Day Headers */}
                <div
                  className={`grid grid-cols-8 border-b transition-colors duration-300 ${
                    isDarkMode ? "border-gray-600" : "border-gray-200"
                  }`}
                >
                  <div
                    className={`p-3 border-r transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <div
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Time
                    </div>
                  </div>
                  {getWeekDays(currentDate).map((day, index) => {
                    const isToday =
                      new Date().toDateString() === day.toDateString();
                    return (
                      <div
                        key={index}
                        className={`p-3 text-center border-r transition-colors duration-300 ${
                          isDarkMode
                            ? `${
                                isToday ? "bg-blue-900" : "bg-gray-700"
                              } border-gray-600`
                            : `${
                                isToday ? "bg-blue-50" : "bg-gray-50"
                              } border-gray-200`
                        }`}
                      >
                        <div
                          className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {dayNames[day.getDay()]}
                        </div>
                        <div
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isToday
                              ? "text-blue-600"
                              : isDarkMode
                                ? "text-white"
                                : "text-gray-900"
                          }`}
                        >
                          {day.getDate()}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Time Slots */}
                {timeSlots.map((hour) => (
                  <div
                    key={hour}
                    className={`grid grid-cols-8 border-b transition-colors duration-300 ${
                      isDarkMode ? "border-gray-600" : "border-gray-200"
                    }`}
                  >
                    <div
                      className={`p-3 border-r transition-colors duration-300 ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <div
                        className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {hour}:00
                      </div>
                    </div>

                    {getWeekDays(currentDate).map((day, dayIndex) => {
                      const dateString = formatDateFromDate(day);
                      const hourBookings = showMyAppointmentsOnly
                        ? getMyAppointmentsForTimeSlot(dateString, hour)
                        : getFilteredBookingsForTimeSlot(dateString, hour);
                      const hourBlockOuts = showMyAppointmentsOnly
                        ? getMyAppointmentBlockOutsForTimeSlot(dateString, hour)
                        : getFilteredBlockOutsForTimeSlot(dateString, hour);

                      return (
                        <TimeSlotCell
                          key={dayIndex}
                          dateString={dateString}
                          hour={hour}
                          hourBookings={hourBookings}
                          hourBlockOuts={hourBlockOuts}
                          serviceColors={serviceColors}
                          serviceColorsFromData={extendedServiceColorsFromData}
                          onCreateBooking={safe(onCreateBooking)}
                          onEditBooking={safe(onShowEditBooking)}
                          onDeleteBooking={safe(onDeleteBooking)}
                          onEditBlockOut={safe(onEditBlockOut)}
                          onDeleteBlockOut={safe(onDeleteBlockOut)}
                          pharmacistNames={pharmacistNames}
                          rooms={roomNames}
                          size="week"
                          isDarkMode={isDarkMode}
                          readOnlyBookings={false}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            {view === "day" && (
              <div
                className={`border rounded-lg overflow-hidden transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-600"
                    : "bg-white border-gray-200"
                }`}
              >
                {/* Day Header */}
                <div
                  className={`p-4 border-b transition-colors duration-300 ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div
                    className={`text-lg font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {dayNames[currentDate.getDay()]},{" "}
                    {currentDate.toLocaleDateString("en-GB")}
                  </div>
                </div>

                {/* Time Slots for Single Day */}
                {timeSlots?.map((hour) => {
                  const dateString = formatDateFromDate(currentDate);
                  const hourBookings = showMyAppointmentsOnly
                    ? getMyAppointmentsForTimeSlot(dateString, hour)
                    : getFilteredBookingsForTimeSlot(dateString, hour);
                  const hourStartBookings = hourBookings.filter((booking) => {
                    if (!booking.time) return false;
                    const [hh] = booking.time.split(":");
                    return parseInt(hh, 10) === hour;
                  });

                  return (
                    <div
                      key={hour}
                      className={`flex border-b transition-colors duration-300 ${
                        isDarkMode ? "border-gray-600" : "border-gray-200"
                      }`}
                    >
                      <div
                        className={`w-20 p-4 border-r transition-colors duration-300 ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {hour}:00
                        </div>
                      </div>

                      <TimeSlotCell
                        dateString={dateString}
                        hour={hour}
                        hourBookings={hourStartBookings}
                        hourBlockOuts={
                          showMyAppointmentsOnly
                            ? getMyAppointmentBlockOutsForTimeSlot(
                                dateString,
                                hour,
                              )
                            : getFilteredBlockOutsForTimeSlot(dateString, hour)
                        }
                        serviceColors={serviceColors}
                        serviceColorsFromData={extendedServiceColorsFromData}
                        onCreateBooking={safe(onCreateBooking)}
                        onEditBooking={safe(onShowEditBooking)}
                        onDeleteBooking={safe(onDeleteBooking)}
                        onEditBlockOut={safe(onEditBlockOut)}
                        onDeleteBlockOut={safe(onDeleteBlockOut)}
                        pharmacistNames={pharmacistNames}
                        rooms={roomNames}
                        size="day"
                        isDarkMode={isDarkMode}
                        readOnlyBookings={false}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Booking Modal */}
      <BookingModal
        isOpen={modalState.isOpen}
        mode={modalState.mode}
        booking={modalState.booking}
        onSave={onSaveFromModal}
        onCancel={closeModal}
        initialDate={modalState.initialDate}
        serviceTypes={serviceTypes}
        pharmacists={pharmacistNames}
        rooms={roomNames}
        isDarkMode={isDarkMode}
        isReadOnly={false}
        onStartConsultation={handleStartConsultation}
        onDelete={onDeleteBooking}
      />

      {/* Conflict Warning Modal */}
      {Boolean(conflictWarning) && (
        <Dialog
          open={true}
          onOpenChange={(open) => {
            if (!open) clearConflictWarning();
          }}
        >
          <DialogContent
            className={`${
              isDarkMode
                ? "bg-gray-800 border-gray-600 text-white"
                : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            <DialogHeader>
              <DialogTitle
                className={`${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Action Not Allowed
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p
                className={`${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
              >
                {conflictWarning}
              </p>
              <div className="flex justify-end">
                <UIButton
                  onClick={clearConflictWarning}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  OK
                </UIButton>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
